import React from 'react';
import { useSocket } from '../../contexts/SocketContext';
import { PlayerRole, formatString, TimerPhase } from '@deadpan/shared';
import { useTranslations } from '../../hooks/useTranslations';
import { useGameWord } from '../../hooks/useGameWord';
import { TimerDisplay } from '../shared/TimerDisplay';
import { getRoleConfig } from '../../utils/roleConfig';
import { DeadpanAnnouncementLine } from './DeadpanAnnouncementLine';

export const DeadpanRoleAssignment: React.FC = () => {
  const { socket, gameState, myRole, myPlayerId } = useSocket();
  const t = useTranslations();
  const { word, definition } = useGameWord(gameState);

  if (!gameState || !word || !myRole) {
    return (
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-[90%] max-w-[600px] border border-white/20 text-center">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4 px-2">
          {!gameState || !word ? t.games.deadpan.loadingGame : t.games.deadpan.waitingForRoles}
        </h2>
        <p className="text-sm sm:text-base text-gray-600 px-2">
          {!gameState || !word ? t.games.deadpan.pleaseWaitRoles : t.games.deadpan.waitingForRoleAssignment}
        </p>
      </div>
    );
  }

  const roleConfig = getRoleConfig(t);
  const config = roleConfig[myRole];
  const isDeadpan = myRole === PlayerRole.DEADPAN;
  const isFactSpeaker = myRole === PlayerRole.FACT_SPEAKER;
  const isWordsmith = myRole === PlayerRole.WORDSMITH;
  const isExplanationPhase = gameState.timer?.phase === TimerPhase.EXPLANATION_DISPLAY;
  // Use localized definition from useGameWord hook instead of myAnswer
  const hasDefinition = isFactSpeaker && definition && isExplanationPhase;

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-[90%] max-w-[600px] border border-white/20 flex flex-col items-center">
      {gameState.timer && <TimerDisplay timer={gameState.timer} />}
      <div className="w-full text-center mb-4 sm:mb-6 md:mb-8">
        <div className={`inline-block px-4 sm:px-6 py-2 sm:py-3 rounded-xl mb-2 sm:mb-3 bg-gradient-to-r ${config.gradient}`}>
          <span className="text-3xl sm:text-4xl block mb-1 sm:mb-2">{config.emoji}</span>
          <h2 className="text-xl sm:text-2xl font-bold text-white">
            {formatString(t.games.deadpan.youAre, { role: config.name })}
          </h2>
        </div>
        <DeadpanAnnouncementLine />
      </div>

      <div className={`w-full rounded-xl p-4 sm:p-6 mb-4 sm:mb-6 border-2 ${
        isDeadpan ? 'bg-purple-50 border-purple-300' :
        isFactSpeaker ? 'bg-green-50 border-green-300' :
        isWordsmith ? 'bg-orange-50 border-orange-300' :
        'bg-blue-50 border-blue-300'
      }`}>
        <h3 className={`text-base sm:text-lg font-semibold mb-2 sm:mb-3 flex items-center gap-2 ${
          isDeadpan ? 'text-purple-800' : 
          isFactSpeaker ? 'text-green-800' : 
          isWordsmith ? 'text-orange-800' :
          'text-blue-800'
        }`}>
          <span className="text-lg sm:text-xl">{config.emoji}</span>
          {t.games.deadpan.yourMission}
        </h3>
        <div className={`text-base sm:text-lg p-3 sm:p-4 rounded-lg font-semibold ${
          isDeadpan ? 'bg-purple-100 text-purple-900' :
          isFactSpeaker ? 'bg-green-100 text-green-900' :
          isWordsmith ? 'bg-orange-100 text-orange-900' :
          'bg-blue-100 text-blue-900'
        }`}>
          {isDeadpan ? t.games.deadpan.makeUpDefinition : 
           isFactSpeaker ? t.roleDescriptions.fact_speaker : 
           isWordsmith ? t.games.deadpan.wordsmithObserverMission :
           t.games.deadpan.detectiveGoal}
        </div>
      </div>

      <div className="w-full bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6 border-2 border-indigo-200">
        <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-2 sm:mb-3 flex items-center gap-2">
          <span className="text-lg sm:text-xl">📖</span>
          {t.games.deadpan.theWord}
        </h3>
        <div className="w-full overflow-x-auto">
          <p className="text-[clamp(1.5rem,8vw,3rem)] font-bold text-gray-800 text-center mb-2 sm:mb-3 whitespace-nowrap">
            {word}
          </p>
        </div>
        {hasDefinition && (
          <div className="mt-4 p-4 rounded-lg bg-green-100 text-green-900">
            <p className="text-sm sm:text-base font-semibold mb-2">{t.games.deadpan.realDefinition}</p>
            <p className="text-lg font-semibold">{definition}</p>
          </div>
        )}
      </div>

      {gameState.room.hostId === myPlayerId && (
        <div className="w-full mt-4 sm:mt-6 pt-4 sm:pt-6 border-t-2 border-gray-200 space-y-3 flex flex-col">
          {!gameState.room.wordsmithId && (
            <button
              onClick={() => socket?.emit('client:change-word')}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 sm:py-4 px-4 sm:px-6 rounded-xl font-bold text-base sm:text-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              🔄 {t.games.deadpan.changeWord}
            </button>
          )}
          {!gameState.timer && (
            <button
              onClick={() => socket?.emit('client:start-new-game')}
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 sm:py-4 px-4 sm:px-6 rounded-xl font-bold text-base sm:text-lg hover:from-green-700 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              🎮 {t.ui.startNewGame}
            </button>
          )}
        </div>
      )}

    </div>
  );
};

